import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import ErrorPage from 'next/error';
import Story from '../../endpoints/Story/Story';
import ContentGrid from '../../grids/ContentGrid';
import Sidebar from '../../components/Sidebar/Sidebar';
import fetcher from '../../utils/fetcher';
import STORYQUERY from '../../endpoints/Story/StoryQuery';
import { parseEmbeddedAssets } from '../../utils/utils';
import { getServerSession } from 'next-auth';
import { authOptions } from 'pages/api/auth/[...nextauth]';
import AdCategoriesContext from 'context/AdCategoriesContext';
import getWordCount from '../../utils/getWordCount';
import setPageCacheHeaders from '../../utils/setPageCacheHeaders';
import getStoryMaxAge from '../../utils/getStoryMaxAge';
import getStoryConfig from '../../endpoints/Story/getStoryConfig';

const StoryPage = ({ data, errorCode, wordcount }) => {
  if (errorCode) return <ErrorPage statusCode={errorCode} />;
  const context = useContext(AdCategoriesContext);

  useEffect(() => {
    if (data.story.primaryCollection) {
      context.setPrimaryCategory(
        data.story?.primaryCollection.canonicalSlug || 'ros'
      );
    }
    if (data.story.collections) {
      context.setAdCategories(
        data.story?.collections.map((coll) => coll.canonicalSlug)
      );
    }
  }, [data.canonicalSlug]);

  return (
    <ContentGrid
      className="story-content-grid"
      sidebar={<Sidebar pageTopic={data.story.title} />}
    >
      <div id="mainContent"></div>
      <Story minimal={false} data={data} wordcount={wordcount} />
    </ContentGrid>
  );
};

export async function getServerSideProps({
  query: { slug, previewToken },
  req,
  res,
}) {
  let data,
    errorCode = null,
    layout = 'default';

  // Fetch story data and session in parallel — getServerSession makes a
  // separate network round-trip to verify the Cognito token. Running it
  // concurrently with the GraphQL query means both complete in the time
  // it takes the slower one, rather than paying the cost of both serially.
  // Site-wide config comes from an in-memory cache, so it's usually instant.
  const [storyResult, sessionResult, configResult] = await Promise.allSettled([
    fetcher(STORYQUERY, {
      contentAreaSlug: process.env.CONTENT_AREA_SLUG,
      slug: slug.join('/'),
      previewToken: previewToken,
    }),
    getServerSession(req, res, authOptions),
    getStoryConfig(),
  ]);

  const session = sessionResult.value ?? null;
  let maxAge;

  if (storyResult.status === 'fulfilled') {
    data = { ...storyResult.value, ...configResult.value };
    // Previews keep the default short TTL so editors see their changes.
    if (!previewToken) maxAge = getStoryMaxAge(data?.story?.publishDate);
    if (data?.story?.embeddedAssets) {
      parseEmbeddedAssets(data.story.embeddedAssets);
    }

    if (!data.story) {
      res.statusCode = 404;
      errorCode = res.statusCode > 200 ? res.statusCode : false;
    } else {
      layout = data?.story?.primaryCollection?.templateName;
    }
    if (
      res &&
      data?.story?.canonicalSlug &&
      data.story.canonicalSlug !== slug.join('/')
    ) {
      // Must precede writeHead: it strips next-auth's cookies off a response
      // the CDN is about to store, and a 301 is heuristically cacheable even
      // without a Cache-Control header. Once writeHead flushes, setHeader and
      // removeHeader throw ERR_HTTP_HEADERS_SENT.
      setPageCacheHeaders(res, session, maxAge);
      res.writeHead(301, {
        Location: `/story/${data.story.canonicalSlug}`,
      });
      res.end();
      // Next skips rendering once the response is sent, but still requires a
      // returned object.
      return { props: {} };
    }
  } else {
    if (res) {
      res.statusCode = 404;
      errorCode = res.statusCode > 200 ? res.statusCode : false;
    }
  }

  // Must run after the session resolves and the status code is assigned:
  // caching is conditional on both.
  setPageCacheHeaders(res, session, maxAge);

  if (typeof layout == 'undefined') {
    layout = 'default';
  }
  const wordcount = getWordCount(data?.story?.body);

  return {
    props: {
      data,
      errorCode,
      layout,
      session,
      resourceType: data?.story?.resourceType || 'story',
      wordcount,
    },
  };
}

StoryPage.propTypes = {
  errorCode: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  data: PropTypes.object,
  wordcount: PropTypes.number,
};

export default StoryPage;
